import React, { createContext, useCallback, useContext, useId, useMemo, useState } from 'react';
import { useTranslation } from '../../i18n';

interface ProductFiltersContextValue {
  expandedId: string | null;
  toggle: (id: string) => void;
}

const ProductFiltersContext = createContext<ProductFiltersContextValue | null>(null);

function useProductFiltersContext() {
  const ctx = useContext(ProductFiltersContext);
  if (!ctx) {
    throw new Error('ProductFilter must be used inside ProductFilters');
  }
  return ctx;
}

export interface ProductFiltersProps {
  children: React.ReactNode;
}

/** Container for product search filters; only one filter panel open at a time. */
export const ProductFilters: React.FC<ProductFiltersProps> = ({ children }) => {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggle = useCallback((id: string) => {
    setExpandedId(current => (current === id ? null : id));
  }, []);

  const value = useMemo(() => ({ expandedId, toggle }), [expandedId, toggle]);

  return (
    <ProductFiltersContext.Provider value={value}>
      <section className="product-filters" aria-label={t('products.filters')}>
        <h3 className="product-filters__heading">{t('products.filters')}</h3>
        <div className="product-filters__list">{children}</div>
      </section>
    </ProductFiltersContext.Provider>
  );
};

export interface ProductFilterProps {
  /** Stable id used for expand/collapse state. */
  id: string;
  summary: React.ReactNode;
  children: React.ReactNode;
}

/** Single collapsible filter row inside {@link ProductFilters}. */
export const ProductFilter: React.FC<ProductFilterProps> = ({ id, summary, children }) => {
  const { expandedId, toggle } = useProductFiltersContext();
  const panelId = useId();
  const expanded = expandedId === id;

  return (
    <div className={`product-filter${expanded ? ' product-filter--expanded' : ''}`}>
      <button
        type="button"
        className="product-filter__summary"
        onClick={() => toggle(id)}
        aria-expanded={expanded}
        aria-controls={panelId}
      >
        <span className="product-filter__summary-text">{summary}</span>
        <span className="product-filter__chevron" aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded && (
        <div id={panelId} className="product-filter__panel" role="region">
          {children}
        </div>
      )}
    </div>
  );
};
